import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Popover } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasSummary, type DependentView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { ObjectInUseDialog } from '../operations/ObjectInUseDialog.tsx';

const LABELS = { duplicate: 'Duplicate', archive: 'Archive', restore: 'Restore' } as const;

/** Row menu of the Canvas list (BB-109): duplicate, archive (only draft or stopped, nothing active using it), restore. */
export function CanvasActions({ environmentId, canvas, layout = 'menu' }: { environmentId: string; canvas: CanvasSummary; layout?: 'menu' | 'toolbar' }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = usePermissions().has('canvas.edit');
  const [open, setOpen] = useState(false);
  const [inUse, setInUse] = useState<DependentView[] | null>(null);
  const run = useMutation({
    mutationFn: (action: CanvasSummary['actions'][number]) => api.canvasAction(environmentId, canvas.id, action),
    onSuccess: async (result, action) => {
      await queryClient.invalidateQueries({ queryKey: ['canvases', environmentId] });
      if (action === 'duplicate') await navigate({ to: '/env/$environmentId/canvas/$canvasId', params: { environmentId, canvasId: result.id } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'object_in_use') setInUse((error.problem.dependents as DependentView[] | undefined) ?? []);
    },
  });
  if (!canEdit) return null;
  const buttons = canvas.actions.map((action) => (
    <Button
      key={action}
      variant="ghost"
      disabled={run.isPending}
      onClick={() => {
        setOpen(false);
        run.mutate(action);
      }}
    >
      {LABELS[action]}
    </Button>
  ));
  return (
    <>
      {layout === 'toolbar' ? (
        <div className="toolbar" role="group" aria-label={`Actions for ${canvas.name}`}>
          {buttons}
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen} label={`Actions for ${canvas.name}`} trigger={<Button variant="ghost" aria-label={`Actions for ${canvas.name}`}>⋯</Button>}>
          <div className="menu">{buttons}</div>
        </Popover>
      )}
      {run.isError && !(run.error instanceof ApiError && run.error.code === 'object_in_use') && <p role="alert">{run.error instanceof ApiError ? run.error.message : 'Something went wrong.'}</p>}
      {inUse && <ObjectInUseDialog open onOpenChange={(o) => !o && setInUse(null)} what={`“${canvas.name}”`} dependents={inUse} />}
    </>
  );
}
